import React, { useState, useEffect } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  CalendarToday,
  AccessTime,
  Group,
} from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext';
import { useFiscalDateRange } from '../hooks/useFiscalDateRange';
import { dailyAttendanceService } from '../services/dailyAttendance.service';
import { branchesService } from '../services/branches.service';
import employeesService from '../services/employees.service';
import { DailyAttendance, CreateDailyAttendanceDto, UpdateDailyAttendanceDto } from '../types/dailyAttendance.types';
import { Branch } from '../types/branches.types';
import { Employee } from '../types/employees.types';
import { toast } from 'react-toastify';

const translations = {
  en: {
    title: 'Daily Attendance',
    titleAr: 'الحضور اليومي',
    date: 'Date',
    branch: 'Branch',
    employee: 'Employee',
    regularHours: 'Regular Hours',
    overtimeHours: 'Overtime Hours',
    publicHolidayHours: 'Holiday Hours',
    totalHours: 'Total Hours',
    notes: 'Notes',
    actions: 'Actions',
    add: 'Add Attendance',
    edit: 'Edit',
    delete: 'Delete',
    refresh: 'Refresh',
    save: 'Save',
    cancel: 'Cancel',
    selectBranch: 'Select Branch',
    selectEmployee: 'Select Employee',
    allBranches: 'All Branches',
    loading: 'Loading...',
    noData: 'No attendance records',
    confirmDelete: 'Are you sure you want to delete this record?',
    addSuccess: 'Attendance added successfully',
    updateSuccess: 'Attendance updated successfully',
    deleteSuccess: 'Attendance deleted successfully',
    error: 'An error occurred',
    totalEmployees: 'Total Employees',
    totalRegularHours: 'Total Regular Hours',
    totalOvertimeHours: 'Total Overtime',
    today: 'Today',
    hours: 'hours',
    records: 'Records',
  },
  ar: {
    title: 'الحضور اليومي',
    titleAr: 'الحضور اليومي',
    date: 'التاريخ',
    branch: 'الفرع',
    employee: 'الموظف',
    regularHours: 'ساعات العمل',
    overtimeHours: 'ساعات إضافية',
    publicHolidayHours: 'ساعات العطل',
    totalHours: 'إجمالي الساعات',
    notes: 'ملاحظات',
    actions: 'الإجراءات',
    add: 'إضافة حضور',
    edit: 'تعديل',
    delete: 'حذف',
    refresh: 'تحديث',
    save: 'حفظ',
    cancel: 'إلغاء',
    selectBranch: 'اختر الفرع',
    selectEmployee: 'اختر الموظف',
    allBranches: 'جميع الفروع',
    loading: 'جاري التحميل...',
    noData: 'لا توجد سجلات حضور',
    confirmDelete: 'هل أنت متأكد من حذف هذا السجل؟',
    addSuccess: 'تم إضافة الحضور بنجاح',
    updateSuccess: 'تم تحديث الحضور بنجاح',
    deleteSuccess: 'تم حذف الحضور بنجاح',
    error: 'حدث خطأ',
    totalEmployees: 'إجمالي الموظفين',
    totalRegularHours: 'إجمالي ساعات العمل',
    totalOvertimeHours: 'إجمالي الوقت الإضافي',
    today: 'اليوم',
    hours: 'ساعة',
    records: 'سجل',
  },
};

const DailyAttendancePage: React.FC = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const isRTL = language === 'ar';
  const { minDate, maxDate } = useFiscalDateRange();

  const [attendances, setAttendances] = useState<DailyAttendance[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedBranch, setSelectedBranch] = useState<number | ''>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const [formData, setFormData] = useState<CreateDailyAttendanceDto>({
    employeeId: 0,
    branchId: 0,
    attendanceDate: new Date().toISOString().split('T')[0],
    regularHours: 8,
    overtimeHours: 0,
    publicHolidayHours: 0,
    notes: '',
  });

  useEffect(() => {
    loadBranches();
    loadEmployees();
    loadAttendances();
  }, []);

  useEffect(() => {
    loadAttendances();
  }, [selectedDate, selectedBranch]);

  const loadBranches = async () => {
    try {
      const data = await branchesService.getAll();
      setBranches(data);
    } catch (error) {
      console.error('Failed to load branches:', error);
      toast.error(t.error);
    }
  };

  const loadEmployees = async () => {
    try {
      const response = await employeesService.getAll();
      setEmployees(response.data);
    } catch (error) {
      console.error('Failed to load employees:', error);
      toast.error(t.error);
    }
  };

  const loadAttendances = async () => {
    setLoading(true);
    try {
      const data = await dailyAttendanceService.getByDate(
        selectedDate,
        selectedBranch || undefined
      );
      setAttendances(data);
    } catch (error) {
      console.error('Failed to load attendances:', error);
      toast.error(t.error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (attendance?: DailyAttendance) => {
    if (attendance) {
      setEditingId(attendance.attendanceId);
      setFormData({
        employeeId: attendance.employeeId,
        branchId: attendance.branchId,
        attendanceDate: attendance.attendanceDate,
        regularHours: attendance.regularHours,
        overtimeHours: attendance.overtimeHours,
        publicHolidayHours: attendance.publicHolidayHours,
        notes: attendance.notes || '',
      });
    } else {
      setEditingId(null);
      setFormData({
        employeeId: 0,
        branchId: selectedBranch || 0,
        attendanceDate: selectedDate,
        regularHours: 8,
        overtimeHours: 0,
        publicHolidayHours: 0,
        notes: '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
  };

  const handleSave = async () => {
    try {
      if (editingId) {
        const updateData: UpdateDailyAttendanceDto = {
          attendanceId: editingId,
          regularHours: formData.regularHours,
          overtimeHours: formData.overtimeHours,
          publicHolidayHours: formData.publicHolidayHours,
          notes: formData.notes,
        };
        await dailyAttendanceService.update(editingId, updateData);
        toast.success(t.updateSuccess);
      } else {
        await dailyAttendanceService.create(formData);
        toast.success(t.addSuccess);
      }
      handleCloseDialog();
      loadAttendances();
    } catch (error) {
      console.error('Failed to save attendance:', error);
      toast.error(t.error);
    }
  };

  const handleDelete = async () => {
    if (deletingId) {
      try {
        await dailyAttendanceService.delete(deletingId);
        toast.success(t.deleteSuccess);
        setDeleteConfirmOpen(false);
        setDeletingId(null);
        loadAttendances();
      } catch (error) {
        console.error('Failed to delete attendance:', error);
        toast.error(t.error);
      }
    }
  };

  const handleOpenDeleteConfirm = (id: number) => {
    setDeletingId(id);
    setDeleteConfirmOpen(true);
  };

  const getTotalStats = () => {
    const totalEmployees = new Set(attendances.map(a => a.employeeId)).size;
    const totalRegular = attendances.reduce((sum, a) => sum + a.regularHours, 0);
    const totalOvertime = attendances.reduce((sum, a) => sum + a.overtimeHours, 0);
    
    return { totalEmployees, totalRegular, totalOvertime };
  };

  const stats = getTotalStats();

  return (
    <Box sx={{ p: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" sx={{ color: '#fff', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccessTime />
          {isRTL ? t.titleAr : t.title}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ bgcolor: '#fff', color: '#667eea', '&:hover': { bgcolor: '#f0f0f0' } }}
          >
            {t.add}
          </Button>
          <IconButton onClick={loadAttendances} sx={{ bgcolor: '#fff', '&:hover': { bgcolor: '#f0f0f0' } }}>
            <RefreshIcon sx={{ color: '#667eea' }} />
          </IconButton>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 300px' }}>
          <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6">{t.totalEmployees}</Typography>
                  <Typography variant="h3" sx={{ fontWeight: 'bold' }}>{stats.totalEmployees}</Typography>
                </Box>
                <Group sx={{ fontSize: 60, opacity: 0.5 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 300px' }}>
          <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: '#fff' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6">{t.totalRegularHours}</Typography>
                  <Typography variant="h3" sx={{ fontWeight: 'bold' }}>{stats.totalRegular.toFixed(1)}</Typography>
                </Box>
                <AccessTime sx={{ fontSize: 60, opacity: 0.5 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 300px' }}>
          <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: '#fff' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6">{t.totalOvertimeHours}</Typography>
                  <Typography variant="h3" sx={{ fontWeight: 'bold' }}>{stats.totalOvertime.toFixed(1)}</Typography>
                </Box>
                <CalendarToday sx={{ fontSize: 60, opacity: 0.5 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ flex: '1 1 200px' }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label={t.date}
                value={selectedDate ? new Date(selectedDate) : null}
                onChange={(date) => setSelectedDate(date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '')}
                format="dd/MM/yyyy"
                minDate={minDate}
                maxDate={maxDate}
                views={['year', 'month', 'day']}
                openTo="day"
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>
          </Box>
          <Box sx={{ flex: '1 1 200px' }}>
            <Autocomplete
              options={branches}
              getOptionLabel={(option) => isRTL ? option.branchNameAr : option.branchName}
              value={branches.find(b => b.branchId === selectedBranch) || null}
              onChange={(_, newValue) => setSelectedBranch(newValue?.branchId || '')}
              renderInput={(params) => <TextField {...params} label={t.branch} />}
              fullWidth
            />
          </Box>
        </Box>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : attendances.length === 0 ? (
          <Box sx={{ textAlign: 'center', p: 4 }}>
            <Typography variant="h6" color="textSecondary">{t.noData}</Typography>
          </Box>
        ) : (
          <Table>
            <TableHead sx={{ bgcolor: '#f5f5f5' }}>
              <TableRow>
                <TableCell><strong>{t.employee}</strong></TableCell>
                <TableCell><strong>{t.branch}</strong></TableCell>
                <TableCell align="center"><strong>{t.regularHours}</strong></TableCell>
                <TableCell align="center"><strong>{t.overtimeHours}</strong></TableCell>
                <TableCell align="center"><strong>{t.publicHolidayHours}</strong></TableCell>
                <TableCell align="center"><strong>{t.totalHours}</strong></TableCell>
                <TableCell><strong>{t.notes}</strong></TableCell>
                <TableCell align="center"><strong>{t.actions}</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {attendances.map((attendance) => {
                const totalHours = attendance.regularHours + attendance.overtimeHours + attendance.publicHolidayHours;
                return (
                  <TableRow key={attendance.attendanceId} hover>
                    <TableCell>{isRTL ? attendance.employeeNameAr : attendance.employeeName}</TableCell>
                    <TableCell>{isRTL ? attendance.branchNameAr : attendance.branchName}</TableCell>
                    <TableCell align="center">
                      <Chip label={attendance.regularHours.toFixed(1)} color="primary" size="small" />
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={attendance.overtimeHours.toFixed(1)} color="warning" size="small" />
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={attendance.publicHolidayHours.toFixed(1)} color="success" size="small" />
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={totalHours.toFixed(1)} color="info" size="small" />
                    </TableCell>
                    <TableCell>{attendance.notes || '-'}</TableCell>
                    <TableCell align="center">
                      <Tooltip title={t.edit}>
                        <IconButton size="small" onClick={() => handleOpenDialog(attendance)} color="primary">
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t.delete}>
                        <IconButton size="small" onClick={() => handleOpenDeleteConfirm(attendance.attendanceId)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? t.edit : t.add}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Autocomplete
              options={employees}
              getOptionLabel={(option) => `${option.employeeCode} - ${isRTL ? option.fullNameAr : option.fullName}`}
              value={employees.find(e => e.employeeId === formData.employeeId) || null}
              onChange={(_, newValue) => setFormData({ ...formData, employeeId: newValue?.employeeId || 0 })}
              renderInput={(params) => <TextField {...params} label={t.employee} fullWidth />}
              disabled={!!editingId}
            />
            <Autocomplete
              options={branches}
              getOptionLabel={(option) => isRTL ? option.branchNameAr : option.branchName}
              value={branches.find(b => b.branchId === formData.branchId) || null}
              onChange={(_, newValue) => setFormData({ ...formData, branchId: newValue?.branchId || 0 })}
              renderInput={(params) => <TextField {...params} label={t.branch} fullWidth />}
              disabled={!!editingId}
            />
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label={t.date}
                value={formData.attendanceDate ? new Date(formData.attendanceDate) : null}
                onChange={(date) => setFormData({ ...formData, attendanceDate: date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '' })}
                format="dd/MM/yyyy"
                minDate={minDate}
                maxDate={maxDate}
                views={['year', 'month', 'day']}
                openTo="day"
                disabled={!!editingId}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>
            <TextField
              fullWidth
              type="number"
              label={t.regularHours}
              value={formData.regularHours}
              onChange={(e) => setFormData({ ...formData, regularHours: Number(e.target.value) })}
              inputProps={{ min: 0, max: 24, step: 0.5 }}
            />
            <TextField
              fullWidth
              type="number"
              label={t.overtimeHours}
              value={formData.overtimeHours}
              onChange={(e) => setFormData({ ...formData, overtimeHours: Number(e.target.value) })}
              inputProps={{ min: 0, max: 24, step: 0.5 }}
            />
            <TextField
              fullWidth
              type="number"
              label={t.publicHolidayHours}
              value={formData.publicHolidayHours}
              onChange={(e) => setFormData({ ...formData, publicHolidayHours: Number(e.target.value) })}
              inputProps={{ min: 0, max: 24, step: 0.5 }}
            />
            <TextField
              fullWidth
              multiline
              rows={2}
              label={t.notes}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t.cancel}</Button>
          <Button onClick={handleSave} variant="contained">{t.save}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>{t.delete}</DialogTitle>
        <DialogContent>
          <Typography>{t.confirmDelete}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>{t.cancel}</Button>
          <Button onClick={handleDelete} color="error" variant="contained">{t.delete}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DailyAttendancePage;